import { useContext, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import ZipProjectService from "@app/projects/zipProjectService";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ZipAnalysisQueryParameters } from "@app/projects/zip-projects/interfaces";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { ZipCoverageMapSelections } from "@app/projects/zip-projects/maps/interfaces";
import SpecialtyDetailFootnote from "@app/projects/results/adequacy/specialtyDetailFootnote";
import { outOfNetworkProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import { UserContext } from "@app/user/UserContext";
import { isMultiZipSelection } from "@app/projects/zip-projects/maps/multiZipSelection";

export interface ZipCoverageMapDetailsData {
  /** The zip-analysis row for the applied zip+specialty (metrics); null while loading or if none matched. */
  row: ZipCodeDisplayModel | null;
  /** The adequacy-requirement footnote (the framing string); null while loading or if none matched. */
  footnote: SpecialtyDetailFootnote | null;
  /** The zip-class aggregate row for the applied zip's classification; the targeting preview baseline. */
  classRow: ZipClassAggregateResultDisplayModel | null;
  loading: boolean;
}

// Thin copy of county's getRegionDetails (NetworkCoverageMapDataContext), adapted for zip. County's region-set
// summaries endpoint isn't authorized for zip, so both reads are zip-native by zip + specialty: metrics from
// getZipCodeResults, the framing string from the shared-helper footnote (getZipAdequacyFootnote).
export const useZipCoverageMapDetails = (
  projectId: number | null,
  selections: ZipCoverageMapSelections | null,
): ZipCoverageMapDetailsData => {
  const zipProjectService = useRef(new ZipProjectService()).current;
  const user = useContext(UserContext);

  const isMultiZip = isMultiZipSelection(selections);
  const enabled = projectId !== null && selections !== null && !isMultiZip;

  // Scope the cache by user + a stable selection blob so one user can't reuse another's cached result.
  const selectionKey = JSON.stringify(
    selections
      ? {
        projectId,
        resultsAsOfId: selections.resultsAsOfId,
        specialtyGroupCode: selections.specialtyGroupCode.key,
        zipCode: selections.serviceArea.zipCode,
      }
      : null,
  );

  const { data: row, isFetching: loadingRow } = useQuery({
    enabled,
    queryFn: async ({ signal }) => {
      // Filter by zip + specialty only; null on the other fields means "no filter" (same convention as
      // getSearchParametersFromFilterSelections). The map only offers published standard run configurations,
      // so ServiceArea is the analysis type.
      const query: ZipAnalysisQueryParameters = {
        limit: 1,
        networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
        offset: 0,
        searchParameters: {
          city: null,
          county: null,
          metPassPercent: null,
          specialtyGroupCode: selections!.specialtyGroupCode.key,
          state: null,
          zipClass: null,
          zipCode: selections!.serviceArea.zipCode,
        },
        sortOptions: [],
      };

      const responses = await zipProjectService.getZipCodeResults(projectId!, selections!.resultsAsOfId, query, signal);
      const matched = responses.find((r) => r.projectRunConfigurationId === selections!.resultsAsOfId);
      const rows = (matched?.data ?? []) as ZipCodeDisplayModel[];
      return rows.find((r) => r.zipCode === selections!.serviceArea.zipCode) ?? rows[0] ?? null;
    },
    queryKey: ["zip-coverage-map", "details-row", user?.userId, selectionKey],
    staleTime: 5 * 60 * 1000,
  });

  const { data: footnote, isFetching: loadingFootnote } = useQuery({
    enabled,
    queryFn: ({ signal }) => zipProjectService.getZipAdequacyFootnote(
      projectId!,
      selections!.resultsAsOfId,
      selections!.serviceArea.zipCode,
      selections!.specialtyGroupCode.key,
      signal,
    ),
    queryKey: ["zip-coverage-map", "details-footnote", user?.userId, selectionKey],
    staleTime: 5 * 60 * 1000,
  });

  // Only the targeting preview consumes this, and only Out of Network providers can be targeted.
  const canTarget = (selections?.providerSources ?? []).some((source) => outOfNetworkProviderNetworkTypes.includes(source));
  const zipClass = row?.zipClass ?? null;

  const { data: classRow } = useQuery({
    enabled: enabled && canTarget && !!zipClass,
    queryFn: async ({ signal }) => {
      const query: ZipAnalysisQueryParameters = {
        limit: 1,
        networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
        offset: 0,
        searchParameters: {
          city: null,
          county: null,
          metOverall: null,
          metPassPercent: null,
          specialtyGroupCode: selections!.specialtyGroupCode.key,
          state: null,
          zipClass,
          zipCode: null,
        },
        sortOptions: [],
      };

      const responses = await zipProjectService.getZipClassResults(projectId!, selections!.resultsAsOfId, query, signal);
      const matched = responses.find((r) => r.projectRunConfigurationId === selections!.resultsAsOfId);
      const rows = (matched?.data ?? []) as ZipClassAggregateResultDisplayModel[];
      return rows.find((r) => r.zipClass === zipClass) ?? rows[0] ?? null;
    },
    queryKey: ["zip-coverage-map", "details-class-row", user?.userId, selectionKey, zipClass],
    staleTime: 5 * 60 * 1000,
  });

  // Disabled queries still return cached single-zip data.
  if (isMultiZip) {
    return { classRow: null, footnote: null, loading: false, row: null };
  }

  return {
    classRow: canTarget ? (classRow ?? null) : null,
    footnote: footnote ?? null,
    loading: loadingRow || loadingFootnote,
    row: row ?? null,
  };
};
